
import { useState, useEffect } from 'react';
import { PACT } from '@/types/pact';

const STORAGE_KEY = 'show-up-pacts';

export const usePACTs = () => {
  const [pacts, setPacts] = useState<PACT[]>([]);

  // Load PACTs from localStorage on mount
  useEffect(() => {
    const savedPacts = localStorage.getItem(STORAGE_KEY);
    if (savedPacts) {
      setPacts(JSON.parse(savedPacts));
    }
  }, []);

  // Save PACTs to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pacts));
  }, [pacts]);

  const createPACT = (name: string, duration: number, maxSkips: number = 3) => {
    const newPACT: PACT = {
      id: crypto.randomUUID(),
      name,
      duration,
      createdAt: new Date().toISOString(),
      completedDays: [],
      skippedDays: [],
      maxSkips,
      isActive: true,
    };
    setPacts(prev => [...prev, newPACT]);
    return newPACT;
  };

  const toggleCompletion = (pactId: string, date: string) => {
    setPacts(prev => prev.map(pact => {
      if (pact.id === pactId) {
        const completedDays = (pact.completedDays || []).includes(date)
          ? (pact.completedDays || []).filter(d => d !== date)
          : [...(pact.completedDays || []), date];
        // Remove from skipped if marking as completed
        const skippedDays = completedDays.includes(date) 
          ? (pact.skippedDays || []).filter(d => d !== date)
          : pact.skippedDays || [];
        return { ...pact, completedDays, skippedDays };
      }
      return pact;
    }));
  };

  const toggleSkip = (pactId: string, date: string) => {
    setPacts(prev => prev.map(pact => {
      if (pact.id === pactId) {
        const skippedDays = (pact.skippedDays || []).includes(date)
          ? (pact.skippedDays || []).filter(d => d !== date)
          : [...(pact.skippedDays || []), date];
        // Remove from completed if marking as skipped
        const completedDays = skippedDays.includes(date)
          ? (pact.completedDays || []).filter(d => d !== date)
          : pact.completedDays || [];
        return { ...pact, completedDays, skippedDays };
      }
      return pact;
    }));
  };

  const getActivePACTs = () => {
    return pacts.filter(pact => {
      const createdDate = new Date(pact.createdAt);
      const daysSinceCreated = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
      return pact.isActive && daysSinceCreated < pact.duration;
    });
  };

  const getTodaysProgress = () => {
    const today = new Date().toISOString().split('T')[0];
    const activePacts = getActivePACTs();
    const completed = activePacts.filter(pact => (pact.completedDays || []).includes(today)).length;
    return { completed, total: activePacts.length };
  };

  const getStreak = (pact: PACT) => {
    const completedDays = pact.completedDays || [];
    if (completedDays.length === 0) return 0;
    
    const sortedDays = [...completedDays].sort().reverse();
    const today = new Date().toISOString().split('T')[0];
    let streak = 0;
    
    // Check if today is completed
    if (sortedDays[0] === today) {
      streak = 1;
      // Count consecutive days backwards
      for (let i = 1; i < sortedDays.length; i++) {
        const currentDate = new Date(sortedDays[i-1]);
        const previousDate = new Date(sortedDays[i]);
        const dayDiff = Math.floor((currentDate.getTime() - previousDate.getTime()) / (1000 * 60 * 60 * 24));
        
        if (dayDiff === 1) {
          streak++;
        } else {
          break;
        }
      }
    }
    
    return streak;
  };

  return {
    pacts,
    createPACT,
    toggleCompletion,
    toggleSkip,
    getActivePACTs,
    getTodaysProgress,
    getStreak,
  };
};
